import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { BackButton, FormField, FormGrid, FormSection, InlineFeedback, PrimaryButton } from 'components/forms';
import { useFeedback } from 'contexts/FeedbackContext';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { MetaConnection } from 'services/api/relifMeta.api';
import {
  useConfigurePixelMutation,
  useGetAdAccountsByBusinessUnitQuery,
  useLazyGetAvailablePixelsQuery,
  useUpdateNameEventsMutation,
} from 'services/api/relifMeta.api';
import { NAVY, RADIUS_CARD, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

import ConnectionGate from './_shared/ConnectionGate';
import { friendlyError } from './_shared/utils';

interface PixelTabProps {
  businessUnitId: number;
  connection: MetaConnection | null;
  isLoading: boolean;
  onSwitchToConnection: () => void;
}

interface AdAccountFull {
  id: number;
  metaAdAccountId: string;
  name: string;
  metaPixelId?: string | null;
  nameEvents?: { BOFU?: string | null } | null;
}

const PixelTab: React.FC<PixelTabProps> = ({ businessUnitId, connection, isLoading, onSwitchToConnection }) => {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} sx={{ color: NAVY }} />
      </Box>
    );
  }

  if (!connection) {
    return <ConnectionGate onSwitch={onSwitchToConnection} surface="pixel" />;
  }

  return <PixelTabBody businessUnitId={businessUnitId} connection={connection} />;
};

const PixelTabBody: React.FC<{ businessUnitId: number; connection: MetaConnection }> = ({
  businessUnitId,
  connection,
}) => {
  const {
    data: accountsData,
    isLoading: accountsLoading,
    refetch,
  } = useGetAdAccountsByBusinessUnitQuery({ businessUnitId, metaConnectionId: connection.id });
  const [getAvailablePixels, { data: pixelsData, isFetching: isLoadingPixels, error: pixelsError }] =
    useLazyGetAvailablePixelsQuery();

  useEffect(() => {
    if (connection.metaBusinessId) {
      getAvailablePixels(connection.metaBusinessId).catch(() => undefined);
    }
  }, [connection.metaBusinessId, getAvailablePixels]);

  const adAccounts = useMemo(() => (accountsData?.adAccounts ?? []) as AdAccountFull[], [accountsData]);

  if (accountsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} sx={{ color: NAVY }} />
      </Box>
    );
  }

  if (adAccounts.length === 0) {
    return (
      <Box sx={{ backgroundColor: SURFACE_CONTAINER_LOWEST, borderRadius: RADIUS_CARD, p: 4, textAlign: 'center' }}>
        <Typography sx={{ fontFamily: 'Jost, sans-serif', color: alpha(NAVY, 0.6) }}>
          No hay cuentas publicitarias asignadas a esta BU todavía.
        </Typography>
        <Typography sx={{ fontFamily: 'Jost, sans-serif', color: alpha(NAVY, 0.5), mt: 0.5, fontSize: '0.875rem' }}>
          Asigna cuentas en la pestaña Cuentas para poder configurar píxel y eventos.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {pixelsError && (
        <InlineFeedback severity="warning">
          No pudimos cargar los píxeles disponibles desde Meta. Puedes seguir editando eventos.
        </InlineFeedback>
      )}

      {adAccounts.map((account) => (
        <AdAccountTrackingCard
          key={account.metaAdAccountId}
          account={account}
          availablePixels={pixelsData?.pixels ?? []}
          isLoadingPixels={isLoadingPixels}
          onSaved={() => {
            refetch().catch(() => undefined);
          }}
        />
      ))}
    </Stack>
  );
};

interface CardProps {
  account: AdAccountFull;
  availablePixels: Array<{ id: string; name: string }>;
  isLoadingPixels: boolean;
  onSaved: () => void;
}

const AdAccountTrackingCard: React.FC<CardProps> = ({ account, availablePixels, isLoadingPixels, onSaved }) => {
  const { notifySaved } = useFeedback();
  const [configurePixel, { isLoading: isSavingPixel }] = useConfigurePixelMutation();
  const [updateNameEvents, { isLoading: isSavingEvents }] = useUpdateNameEventsMutation();

  const [pixelId, setPixelId] = useState(account.metaPixelId ?? '');
  const [bofu, setBofu] = useState(account.nameEvents?.BOFU ?? '');
  const [error, setError] = useState<string | null>(null);

  const initialPixel = account.metaPixelId ?? '';
  const initialBofu = account.nameEvents?.BOFU ?? '';

  const pixelChanged = pixelId !== initialPixel;
  const eventsChanged = bofu !== initialBofu;
  const isSaving = isSavingPixel || isSavingEvents;

  const handleSave = useCallback(async () => {
    setError(null);
    try {
      const tasks: Promise<unknown>[] = [];
      if (pixelChanged && pixelId) {
        tasks.push(configurePixel({ metaAdAccountId: account.metaAdAccountId, metaPixelId: pixelId }).unwrap());
      }
      if (eventsChanged) {
        tasks.push(
          updateNameEvents({
            metaAdAccountId: account.metaAdAccountId,
            nameEvents: {
              BOFU: bofu.trim() || undefined,
            },
          }).unwrap()
        );
      }
      if (tasks.length === 0) return;
      await Promise.all(tasks);
      notifySaved('Tracking actualizado');
      onSaved();
    } catch (err) {
      setError(friendlyError(err, 'No pudimos guardar la configuración. Intenta nuevamente.'));
    }
  }, [
    pixelChanged,
    pixelId,
    eventsChanged,
    bofu,
    account.metaAdAccountId,
    configurePixel,
    updateNameEvents,
    notifySaved,
    onSaved,
  ]);

  const isComplete = !!account.metaPixelId && !!account.nameEvents?.BOFU;

  return (
    <FormSection
      eyebrow={isComplete ? 'Configurada' : 'Pendiente'}
      title={account.name}
      description={`Configura el píxel y los eventos del embudo para esta cuenta. ID: ${account.metaAdAccountId}`}
    >
      {error && (
        <Box sx={{ mb: 2 }}>
          <InlineFeedback severity="error">{error}</InlineFeedback>
        </Box>
      )}

      <FormGrid columns={2}>
        <FormField
          select
          label="Meta Pixel"
          value={pixelId}
          onChange={(e) => setPixelId(String(e.target.value))}
          placeholder={isLoadingPixels ? 'Cargando píxeles…' : 'Selecciona un píxel'}
          disabled={availablePixels.length === 0 && !pixelId}
          hint={
            availablePixels.length === 0 && !isLoadingPixels && !pixelId
              ? 'No hay píxeles disponibles en este Meta Business.'
              : undefined
          }
        >
          {pixelId && !availablePixels.some((p) => p.id === pixelId) && (
            <MenuItem key={pixelId} value={pixelId}>
              Píxel actual ({pixelId})
            </MenuItem>
          )}
          {availablePixels.map((pixel) => (
            <MenuItem key={pixel.id} value={pixel.id}>
              {pixel.name}
            </MenuItem>
          ))}
        </FormField>
        <Box />
      </FormGrid>

      <Box sx={{ mt: 3 }}>
        <Typography
          sx={{
            fontFamily: 'Jost, sans-serif',
            fontSize: '0.6875rem',
            fontWeight: 600,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: alpha(NAVY, 0.55),
            mb: 1,
          }}
        >
          Evento del embudo
        </Typography>
        <FormGrid columns={2}>
          <FormField
            label="BOFU"
            value={bofu}
            onChange={(e) => setBofu(e.target.value)}
            placeholder="Cliente potencial"
            hint="Bottom of funnel — evento de conversión"
            optional
          />
          <Box />
        </FormGrid>
      </Box>

      <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
        <PrimaryButton
          onClick={() => {
            handleSave().catch(() => undefined);
          }}
          disabled={isSaving || (!pixelChanged && !eventsChanged)}
        >
          {isSaving ? 'Guardando…' : 'Guardar cambios'}
        </PrimaryButton>
        {(pixelChanged || eventsChanged) && (
          <BackButton
            onClick={() => {
              setPixelId(initialPixel);
              setBofu(initialBofu);
              setError(null);
            }}
          >
            Descartar
          </BackButton>
        )}
      </Stack>
    </FormSection>
  );
};

export default PixelTab;
